import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router'
import type { Route } from './+types/root'
import { AuthProvider } from '@/components/AuthProvider'
import { AppTooltipProvider } from '@/components/providers/AppTooltipProvider'
import { useSyncExternalStore, useEffect } from 'react'
import { getThemeServerSnapshot, getThemeSnapshot, subscribeTheme, resolveTheme } from '@/components/theme/themeStore'
import './app.css'

// Importante: este script corre ANTES de que cargue el CSS, para evitar "parpadeo" de tema.
// Debe mantenerse alineado con `resolveTheme()` (mismo STORAGE_KEY y defaults).
const themeInitScript = `(function(){try{var k='alumni-theme';var t=localStorage.getItem(k);var dark=(t!=='light');document.documentElement.classList.toggle('dark',dark);}catch(e){document.documentElement.classList.add('dark')}})();`

export const links: Route.LinksFunction = () => [
	{ rel: 'icon', href: '/icono.svg' },
	{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
	{
		rel: 'preconnect',
		href: 'https://fonts.gstatic.com',
		crossOrigin: 'anonymous'
	},
	{
		rel: 'stylesheet',
		href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap'
	}
]

export function Layout() {
	// Mantener React alineado con la clase `dark` del <html>, para que no la "pise" durante hidratación/rerenders.
	const theme = useSyncExternalStore(subscribeTheme, getThemeSnapshot, getThemeServerSnapshot)
	const htmlClass = theme === 'dark' ? 'dark' : undefined

	// Si por alguna razón el init-script no corrió, forzamos una resolución al montar (solo cliente).
	useEffect(() => {
		const stored = (() => {
			try {
				const v = localStorage.getItem('alumni-theme')
				return v === 'dark' || v === 'light' ? v : null
			} catch {
				return null
			}
		})()
		if (!stored && !document.documentElement.classList.contains('dark')) {
			document.documentElement.classList.toggle('dark', resolveTheme() === 'dark')
		}
	}, [])
	return (
		<html lang="es" className={htmlClass} suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="min-h-screen antialiased">
				<AuthProvider>
					<AppTooltipProvider>
						<Outlet />
					</AppTooltipProvider>
				</AuthProvider>
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

/**
 * Ruta raíz: encadenar hacia el resto de `routes.ts`. El `Layout` ya envuelve con `AuthProvider`.
 */
export default function App() {
	return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	let message = 'Oops!'
	let details = 'An unexpected error occurred.'
	let stack: string | undefined

	if (isRouteErrorResponse(error)) {
		message = error.status === 404 ? '404' : 'Error'
		details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details
	} else if (import.meta.env.DEV && error && error instanceof Error) {
		details = error.message
		stack = error.stack
	}

	return (
		<main className="pt-16 p-4 container mx-auto">
			<h1>{message}</h1>
			<p>{details}</p>
			{stack && (
				<pre className="w-full p-4 overflow-x-auto">
					<code>{stack}</code>
				</pre>
			)}
		</main>
	)
}
